import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import InputPanel from './components/InputPanel';
import EditPreviewModal from './components/EditPreviewModal';
import { transformContent } from '../../services/openaiService';
import { publishToLinkedIn, publishToTwitter, validateToken, getUserTokens } from '../../services/publishService';
import { saveContent } from '../../services/contentService';
import UpgradeModal from '../../components/ui/UpgradeModal';
import ContentTransferAnimation from '../../components/ui/ContentTransferAnimation';
import SinglePlatformAnimation from '../../components/ui/SinglePlatformAnimation';
import Icon from '../../components/AppIcon';
import { useToast } from '../../components/ui/Toast';
import { friendlyError } from '../../utils/errorMessages';
import { getUsageDisplay, canUseFeature } from '../../services/subscriptionService';
import { createScheduledPost } from '../../services/scheduleService';

// Strip HTML tags and entities from old Quill content stored in localStorage
const stripHtml = (str) => {
  if (!str) return '';
  if (!str.includes('<') && !str.includes('&')) return str;
  try {
    const div = document.createElement('div');
    div.innerHTML = str;
    return (div.textContent || div.innerText || '').trim();
  } catch {
    return str;
  }
};

const IdeaRefiner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, subscription, refreshUsage } = useAuth();
  const toast = useToast();

  // Input state
  const [inputMode, setInputMode] = useState('ai');
  const [roughIdea, setRoughIdea] = useState('');
  const [codeSnippet, setCodeSnippet] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('mentor');
  const [availableStyles, setAvailableStyles] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Chat-like AI response state
  const [aiResponse, setAiResponse] = useState(null); // { linkedin, twitter }

  // Modal state
  const [showModal, setShowModal] = useState(false);

  // Content state (editable in modal)
  const [linkedinContent, setLinkedinContent] = useState('');
  const [twitterContent, setTwitterContent] = useState('');

  // Platform & publishing
  const [selectedPlatforms, setSelectedPlatforms] = useState({ linkedin: true, twitter: true });
  const [tokens, setTokens] = useState({ linkedin: null, twitter: null });
  const [tokenStatus, setTokenStatus] = useState({ linkedin: 'checking', twitter: 'checking' });
  const [publishing, setPublishing] = useState({ linkedin: false, twitter: false, unified: false });
  const [publishedPlatforms, setPublishedPlatforms] = useState({ linkedin: false, twitter: false });
  const [contentPostId, setContentPostId] = useState(null);

  // Images
  const [uploadedImages, setUploadedImages] = useState({ linkedin: [], twitter: [] });
  const [imagePreviewUrls, setImagePreviewUrls] = useState({ linkedin: [], twitter: [] });

  // Published post IDs (for linking to actual posts)
  const [publishedPostIds, setPublishedPostIds] = useState({ linkedin: null, twitter: null });

  // Animations
  const [showAnimation, setShowAnimation] = useState(false);
  const [showSingleAnimation, setShowSingleAnimation] = useState(false);
  const [currentAnimationPlatform, setCurrentAnimationPlatform] = useState(null);
  const [animationStatus, setAnimationStatus] = useState(null);

  // Upgrade modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalData, setUpgradeModalData] = useState({});

  // Scheduling
  const [scheduling, setScheduling] = useState(false);
  const [scheduledInfo, setScheduledInfo] = useState(null);

  // Reset session — called from "New Post" sidebar button or after all published
  const resetSession = useCallback(() => {
    setRoughIdea('');
    setCodeSnippet('');
    setShowCodeInput(false);
    setAiResponse(null);
    setShowModal(false);
    setLinkedinContent('');
    setTwitterContent('');
    setSelectedPlatforms({ linkedin: true, twitter: true });
    setPublishing({ linkedin: false, twitter: false, unified: false });
    setPublishedPlatforms({ linkedin: false, twitter: false });
    setPublishedPostIds({ linkedin: null, twitter: null });
    setUploadedImages({ linkedin: [], twitter: [] });
    setImagePreviewUrls({ linkedin: [], twitter: [] });
    setContentPostId(null);
    setError(null);
    setIsGenerating(false);
    setScheduling(false);
    setScheduledInfo(null);
    localStorage.removeItem('idea_refiner_input');
  }, []);

  // Handle "New Post" navigation from sidebar
  useEffect(() => {
    if (location.state?.newSession) {
      resetSession();
      // Clear the state so refreshing doesn't re-trigger
      navigate('/idea-refiner', { replace: true, state: {} });
    }
  }, [location.state?.newSession, resetSession, navigate]);

  // Load writing styles + persisted input
  useEffect(() => {
    const predefinedStyles = [
      { value: 'mentor', label: 'The Mentor', description: 'Teaching and guiding tone', tone: 'Clear, educational, supportive', technicalDepth: 'Medium', engagement: 'Questions and teaching moments' },
      { value: 'deep-dive', label: 'Deep-Dive Dev', description: 'Technical and detailed', tone: 'Technical, thorough, analytical', technicalDepth: 'High', engagement: 'Technical discussions and debates' },
      { value: 'minimalist', label: 'The Minimalist', description: 'Concise and impactful', tone: 'Brief, direct, impactful', technicalDepth: 'Low to Medium', engagement: 'Quick insights and actionable tips' }
    ];

    const customStylesJSON = localStorage.getItem('custom_writing_styles');
    const customStyles = customStylesJSON ? JSON.parse(customStylesJSON) : [];
    const formattedCustomStyles = customStyles?.map(style => ({
      value: style?.id, label: style?.name, description: style?.description,
      tone: style?.tone, technicalDepth: style?.technicalDepth, engagement: style?.engagement, isCustom: true
    }));
    setAvailableStyles([...predefinedStyles, ...formattedCustomStyles]);

    if (userProfile?.default_writing_style) setSelectedStyle(userProfile.default_writing_style);

    // Load persisted input — strip HTML from old Quill content
    const persistedJSON = localStorage.getItem('idea_refiner_input');
    if (persistedJSON) {
      try {
        const persisted = JSON.parse(persistedJSON);
        setRoughIdea(stripHtml(persisted.roughIdea || ''));
        setCodeSnippet(persisted.codeSnippet || '');
        if (persisted.codeSnippet) setShowCodeInput(true);
        if (!userProfile?.default_writing_style && persisted.selectedStyle) setSelectedStyle(persisted.selectedStyle);
      } catch { localStorage.removeItem('idea_refiner_input'); }
    }
  }, [userProfile]);

  // Persist input (plain text only now)
  useEffect(() => {
    if (roughIdea.trim() || codeSnippet.trim()) {
      localStorage.setItem('idea_refiner_input', JSON.stringify({
        roughIdea, codeSnippet, selectedStyle, lastSaved: new Date().toISOString()
      }));
    }
  }, [roughIdea, codeSnippet, selectedStyle]);

  // Check tokens on mount
  useEffect(() => { checkTokens(); }, [userProfile]);

  const checkTokens = async () => {
    try {
      const userTokens = await getUserTokens(userProfile?.id);
      setTokens(userTokens);
      for (const p of ['linkedin', 'twitter']) {
        if (userTokens?.[p]?.token) {
          const v = await validateToken(p, userTokens[p].token);
          setTokenStatus(prev => ({ ...prev, [p]: v?.valid ? 'valid' : 'invalid' }));
        } else {
          setTokenStatus(prev => ({ ...prev, [p]: 'missing' }));
        }
      }
    } catch { setTokenStatus({ linkedin: 'error', twitter: 'error' }); }
  };

  const usageText = getUsageDisplay('ai_generate', subscription);
  const canPublishFeature = canUseFeature('publish', subscription);

  const saveContentToDashboard = async () => {
    try {
      const saved = await saveContent({
        linkedin: linkedinContent, twitter: twitterContent,
        originalIdea: roughIdea || 'Untitled', codeSnippet: codeSnippet || null,
        writingStyle: selectedStyle || null, user_id: userProfile?.id
      });
      setContentPostId(saved?.id);
      return saved?.id;
    } catch (err) { console.error('Error saving:', err); return null; }
  };

  // AI Generation
  const handleGenerate = async () => {
    if (subscription && !canUseFeature('ai_generate', subscription)) {
      const used = subscription.usage?.ai_generations ?? 0;
      const limit = subscription.limits?.ai_generations_per_month ?? 5;
      setUpgradeModalData({ feature: 'ai_generate', used, limit, daysUntilReset: subscription.daysUntilReset || 30 });
      setShowUpgradeModal(true);
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      if (!roughIdea?.trim()) throw new Error('Please enter some content before generating.');
      const styleDetails = availableStyles?.find(s => s?.value === selectedStyle);
      const result = await transformContent(roughIdea, codeSnippet, selectedStyle, styleDetails, '', userProfile?.github_url || '');

      const response = { linkedin: result?.linkedin || '', twitter: result?.twitter || '' };
      setAiResponse(response);
      setLinkedinContent(response.linkedin);
      setTwitterContent(response.twitter);
      setShowModal(true);
      refreshUsage?.({ ai_generations: 1 });
    } catch (err) {
      const msg = err?.message || '';
      if (err?.type === 'LimitReached' || msg.includes('free generations') || msg.includes('limit_reached')) {
        setUpgradeModalData({
          feature: 'ai_generate', used: err?.used ?? 0, limit: err?.limit ?? 5,
          daysUntilReset: err?.daysUntilReset ?? 30, message: msg,
        });
        setShowUpgradeModal(true);
      } else {
        setError(msg);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Regenerate
  const [isRegenerating, setIsRegenerating] = useState(false);
  const regenLimitReached = subscription && !canUseFeature('ai_generate', subscription);
  const handleRegenerate = async (feedback = '') => {
    if (regenLimitReached) {
      setUpgradeModalData({ feature: 'ai_generate', used: subscription.usage?.ai_generations ?? 0, limit: subscription.limits?.ai_generations_per_month ?? 5, daysUntilReset: subscription.daysUntilReset || 30 });
      setShowUpgradeModal(true);
      return;
    }
    setIsRegenerating(true);
    try {
      const styleDetails = availableStyles?.find(s => s?.value === selectedStyle);
      const additionalInstructions = feedback || '';
      const result = await transformContent(roughIdea, codeSnippet, selectedStyle, styleDetails, additionalInstructions, userProfile?.github_url || '');
      const response = { linkedin: result?.linkedin || '', twitter: result?.twitter || '' };
      setAiResponse(response);
      setLinkedinContent(response.linkedin);
      setTwitterContent(response.twitter);
      refreshUsage?.({ ai_generations: 1 });
      toast.success('Content regenerated!');
    } catch (err) {
      toast.error(err?.message || 'Failed to regenerate.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Manual bypass
  const handleContinueManual = () => {
    if (!roughIdea?.trim()) return;
    const content = roughIdea;
    setLinkedinContent(content);
    setTwitterContent(content.length > 280 ? content.substring(0, 280) : content);
    setAiResponse({ linkedin: content, twitter: content.length > 280 ? content.substring(0, 280) : content, isManual: true });
    setShowModal(true);
  };

  // Open existing response in modal
  const handleOpenResponse = () => {
    setShowModal(true);
  };

  // Image upload callback
  const handleImagesUploaded = (platform, newImages) => {
    setUploadedImages(prev => ({
      ...prev,
      [platform]: [...(prev[platform] || []), ...newImages]
    }));
  };

  const handleImagePreviewAdded = (platform, previews, replace = false) => {
    setImagePreviewUrls(prev => ({
      ...prev,
      [platform]: replace ? previews : [...(prev[platform] || []), ...previews]
    }));
  };

  const handleImageRemoved = (platform, index) => {
    setUploadedImages(prev => ({
      ...prev,
      [platform]: (prev[platform] || []).filter((_, i) => i !== index)
    }));
    setImagePreviewUrls(prev => ({
      ...prev,
      [platform]: (prev[platform] || []).filter((_, i) => i !== index)
    }));
  };

  // Publishing
  const handlePublish = async (platform) => {
    if (publishedPlatforms?.[platform]) { toast.warning(`Already published to ${platform === 'linkedin' ? 'LinkedIn' : 'X'}.`); return; }
    setPublishing(prev => ({ ...prev, [platform]: true }));
    setCurrentAnimationPlatform(platform);
    setAnimationStatus(null);
    setShowSingleAnimation(true);

    try {
      let postId = contentPostId;
      if (!postId) postId = await saveContentToDashboard();
      if (tokenStatus?.[platform] !== 'valid') throw new Error(`Please connect your ${platform === 'linkedin' ? 'LinkedIn' : 'X'} account first`);

      const imgs = uploadedImages[platform] || [];
      let result;
      if (platform === 'linkedin') result = await publishToLinkedIn(linkedinContent, tokens?.linkedin?.token, postId, imgs);
      else result = await publishToTwitter(twitterContent, tokens?.twitter?.token, postId, imgs);

      setAnimationStatus('success');
      setPublishedPlatforms(prev => ({ ...prev, [platform]: true }));
      if (result?.postId) setPublishedPostIds(prev => ({ ...prev, [platform]: result.postId }));
      toast.success(`Published to ${platform === 'linkedin' ? 'LinkedIn' : 'X'} successfully!`);
      refreshUsage?.({ publishes: 1 });
    } catch (error) {
      if (error?.type === 'LIMIT_REACHED' || error?.message?.includes('limit_reached')) {
        setAnimationStatus('error');
        setTimeout(() => {
          setShowSingleAnimation(false);
          setUpgradeModalData({ feature: 'publish', used: error.used ?? 0, limit: error.limit ?? 3, daysUntilReset: error.daysUntilReset ?? 30, message: error.message });
          setShowUpgradeModal(true);
        }, 300);
        setPublishing(prev => ({ ...prev, [platform]: false }));
        return;
      }
      setAnimationStatus('error');
      toast.error(friendlyError(error));
    } finally {
      setPublishing(prev => ({ ...prev, [platform]: false }));
    }
  };

  const handleUnifiedPublish = async () => {
    if (tokenStatus?.linkedin !== 'valid' && tokenStatus?.twitter !== 'valid') { toast.error('Connect at least one account.'); return; }
    setPublishing(prev => ({ ...prev, unified: true }));
    setShowAnimation(true);

    try {
      let postId = contentPostId;
      if (!postId) postId = await saveContentToDashboard();
      const promises = [];
      if (selectedPlatforms?.linkedin && tokenStatus?.linkedin === 'valid' && linkedinContent) {
        promises.push(publishToLinkedIn(linkedinContent, tokens?.linkedin?.token, postId, uploadedImages.linkedin || []).then(() => ({ platform: 'linkedin', success: true })).catch(e => ({ platform: 'linkedin', success: false, error: e })));
      }
      if (selectedPlatforms?.twitter && tokenStatus?.twitter === 'valid' && twitterContent) {
        promises.push(publishToTwitter(twitterContent, tokens?.twitter?.token, postId, uploadedImages.twitter || []).then(() => ({ platform: 'twitter', success: true })).catch(e => ({ platform: 'twitter', success: false, error: e })));
      }
      const results = await Promise.all(promises);
      let ok = 0;
      results.forEach(({ platform, success }) => { if (success) { ok++; setPublishedPlatforms(prev => ({ ...prev, [platform]: true })); } });
      if (ok === results.length) toast.success(`Published to ${ok} platform${ok > 1 ? 's' : ''}!`);
      else if (ok > 0) toast.warning(`Published to ${ok}/${results.length} platforms.`);
      else toast.error('Publishing failed.');
      refreshUsage?.(ok > 0 ? { publishes: 1 } : null);
    } catch (error) { toast.error(friendlyError(error)); }
    finally { setPublishing(prev => ({ ...prev, unified: false })); }
  };

  // Scheduling
  const handleSchedule = async (scheduledAt, timezone, schedulePlatforms) => {
    // Client-side quota hint
    if (!canUseFeature('schedule', subscription)) {
      setUpgradeModalData({
        feature: 'schedule',
        used: subscription?.usage?.scheduled_posts ?? 0,
        limit: subscription?.limits?.scheduled_posts_per_month ?? 1,
        daysUntilReset: subscription?.daysUntilReset ?? 30,
      });
      setShowUpgradeModal(true);
      return;
    }

    // Use platforms selected in the schedule modal (falls back to selectedPlatforms)
    const platformsToSchedule = schedulePlatforms || selectedPlatforms;

    setScheduling(true);
    try {
      let postId = contentPostId;
      if (!postId) postId = await saveContentToDashboard();

      // Collect image data only for selected platforms
      const imageData = [
        ...(platformsToSchedule.linkedin ? (uploadedImages.linkedin || []).map(img => ({ ...img, platform: 'linkedin' })) : []),
        ...(platformsToSchedule.twitter ? (uploadedImages.twitter || []).map(img => ({ ...img, platform: 'twitter' })) : []),
      ];

      await createScheduledPost({
        linkedinContent: platformsToSchedule.linkedin ? linkedinContent : null,
        twitterContent: platformsToSchedule.twitter ? twitterContent : null,
        platforms: platformsToSchedule,
        scheduledAt,
        timezone,
        contentPostId: postId,
        imageData,
      });

      // Build a friendly platform label for the toast
      const platformNames = [
        platformsToSchedule.linkedin && 'LinkedIn',
        platformsToSchedule.twitter && 'X',
      ].filter(Boolean).join(' & ');

      setScheduledInfo({ scheduledAt, timezone, platforms: platformsToSchedule });
      toast.success(`Post scheduled to ${platformNames} for ${new Date(scheduledAt).toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${new Date(scheduledAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`);
      refreshUsage?.({ scheduled_posts: 1 });
    } catch (error) {
      if (error?.code === 'limit_reached') {
        setUpgradeModalData({
          feature: 'schedule',
          used: error.used ?? 0,
          limit: error.limit ?? 1,
          daysUntilReset: error.daysUntilReset ?? 30,
          message: error.message,
        });
        setShowUpgradeModal(true);
      } else {
        toast.error(friendlyError(error));
      }
    } finally {
      setScheduling(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ContentTransferAnimation isAnimating={showAnimation} onAnimationComplete={() => setShowAnimation(false)}
        platforms={[...(selectedPlatforms?.linkedin && tokenStatus?.linkedin === 'valid' ? ['linkedin'] : []), ...(selectedPlatforms?.twitter && tokenStatus?.twitter === 'valid' ? ['twitter'] : [])]}
        duration={3000} />
      <SinglePlatformAnimation isAnimating={showSingleAnimation} onAnimationComplete={() => { setShowSingleAnimation(false); setAnimationStatus(null); }}
        platform={currentAnimationPlatform} status={animationStatus} />

      <main className="px-4 md:px-6 lg:px-8 pb-12 flex flex-col min-h-[calc(100vh-64px)]">
        <div className="max-w-3xl mx-auto w-full flex-1 flex flex-col">
          {/* Usage badge — top right */}
          {usageText && subscription?.tier !== 'pro' && (
            <div className="flex justify-end pt-4">
              <span className="text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full">{usageText}</span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center justify-between">
              <p className="text-sm text-red-400">{error}</p>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300"><Icon name="XCircle" size={14} /></button>
            </div>
          )}

          {/* Input Area — centered vertically when no AI response yet */}
          <div className={`flex flex-col items-center w-full ${!aiResponse ? 'flex-1 justify-center' : 'mt-8'}`}>
            <InputPanel
              roughIdea={roughIdea}
              setRoughIdea={setRoughIdea}
              codeSnippet={codeSnippet}
              setCodeSnippet={setCodeSnippet}
              showCodeInput={showCodeInput}
              setShowCodeInput={setShowCodeInput}
              inputMode={inputMode}
              setInputMode={setInputMode}
              selectedStyle={selectedStyle}
              setSelectedStyle={setSelectedStyle}
              availableStyles={availableStyles}
              onGenerate={handleGenerate}
              onContinueManual={handleContinueManual}
              isGenerating={isGenerating}
            />
          </div>

          {/* AI Response — chat-like, shown below input after generation */}
          {aiResponse && !showModal && (
            <div className="mt-6 max-w-3xl mx-auto w-full">
              <button
                onClick={handleOpenResponse}
                className="w-full text-left group"
              >
                <div className="flex items-start gap-3">
                  {/* AI Avatar */}
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Sparkles" size={14} className="text-muted-foreground" />
                  </div>

                  {/* Response Card */}
                  <div className="flex-1 bg-card border border-border rounded-xl p-4 group-hover:border-muted-foreground transition-colors cursor-pointer">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        {aiResponse.isManual ? 'Your content' : 'AI Generated Content'}
                      </span>
                      <span className="text-xs text-muted-foreground group-hover:text-foreground flex items-center gap-1 transition-colors">
                        Click to edit & publish
                        <Icon name="ArrowUpRight" size={12} />
                      </span>
                    </div>

                    {/* LinkedIn Preview Snippet */}
                    {aiResponse.linkedin && (
                      <div className="mb-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon name="Linkedin" size={12} color="#0A66C2" />
                          <span className="text-xs text-muted-foreground">LinkedIn</span>
                        </div>
                        <p className="text-sm text-foreground line-clamp-2">{aiResponse.linkedin}</p>
                      </div>
                    )}

                    {/* Twitter Preview Snippet */}
                    {aiResponse.twitter && (
                      <div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon name="X" size={12} className="text-foreground" />
                          <span className="text-xs text-muted-foreground">X</span>
                        </div>
                        <p className="text-sm text-foreground line-clamp-2">{aiResponse.twitter}</p>
                      </div>
                    )}

                    {/* Published badges */}
                    {(publishedPlatforms.linkedin || publishedPlatforms.twitter) && (
                      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border">
                        {publishedPlatforms.linkedin && (
                          <span className="flex items-center gap-1 text-xs text-emerald-400"><Icon name="CheckCircle" size={12} /> LinkedIn</span>
                        )}
                        {publishedPlatforms.twitter && (
                          <span className="flex items-center gap-1 text-xs text-emerald-400"><Icon name="CheckCircle" size={12} /> X</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Edit & Preview Modal */}
      <EditPreviewModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        linkedinContent={linkedinContent}
        setLinkedinContent={setLinkedinContent}
        twitterContent={twitterContent}
        setTwitterContent={setTwitterContent}
        selectedPlatforms={selectedPlatforms}
        setSelectedPlatforms={setSelectedPlatforms}
        tokenStatus={tokenStatus}
        tokens={tokens}
        uploadedImages={uploadedImages}
        imagePreviewUrls={imagePreviewUrls}
        onImagesUploaded={handleImagesUploaded}
        onImagePreviewAdded={handleImagePreviewAdded}
        onImageRemoved={handleImageRemoved}
        contentPostId={contentPostId}
        saveContentToDashboard={saveContentToDashboard}
        publishing={publishing}
        publishedPlatforms={publishedPlatforms}
        publishedPostIds={publishedPostIds}
        onPublish={handlePublish}
        onUnifiedPublish={handleUnifiedPublish}
        onRegenerate={handleRegenerate}
        isRegenerating={isRegenerating}
        regenDisabled={regenLimitReached}
        regenDisabledReason={regenLimitReached ? 'You\'ve used all your free generations this month. Upgrade to Pro for more.' : ''}
        onSchedule={handleSchedule}
        scheduling={scheduling}
        scheduledInfo={scheduledInfo}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeModalData.feature}
        used={upgradeModalData.used}
        limit={upgradeModalData.limit}
        daysUntilReset={upgradeModalData.daysUntilReset}
        message={upgradeModalData.message}
      />
    </div>
  );
};

export default IdeaRefiner;
